import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient as api } from '../services/api';
import { PublicIntakeForm } from './PublicIntakeForm';
import CodeErrorPage from './CodeErrorPage';
import './ClientForm.css';

interface FormSession {
  id: string;
  formId: string;
  formData: any;
  isSubmitted: boolean;
  submittedAt: string | null;
  lastUpdated: string;
  expiresAt: string;
}

const ClientForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showErrorPage, setShowErrorPage] = useState(false);

  useEffect(() => {
    if (code) {
      loadFormData();
    } else {
      setError('No access code provided');
      setIsLoading(false);
    }
  }, [code]);

  const loadFormData = async () => {
    try {
      const response = await api.loadFormByCode(code!);
      
      if (response.success) {
        const sessionData = response.session;
        console.log('📂 Loaded session data:', sessionData);
        console.log('📝 Form data from session:', sessionData.formData);
        
        setSession(sessionData);
        setFormData(sessionData.formData || {});
      } else {
        setError('Failed to load form data');
      }
    } catch (error: any) {
      console.error('Error loading form:', error);
      const errorMessage = error.message || 'Failed to load form';
      setError(errorMessage);
      
      // Check if this is a deactivated/expired/invalid code error
      const lowerError = errorMessage.toLowerCase();
      if (lowerError.includes('deactivated') || 
          lowerError.includes('expired') || 
          lowerError.includes('invalid')) {
        setShowErrorPage(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveFormData = async (data: any) => {
    try {
      await api.saveFormData(code!, data);
      console.log('Form saved successfully');
    } catch (error) {
      console.error('Save failed:', error);
      throw error; // Re-throw so caller can handle it
    }
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      console.log('🚀 Starting form submission...');
      console.log('📝 Form data being submitted:', JSON.stringify(formData, null, 2));
      console.log('🔑 Code being used:', code);
      
      // Submit the form with current form data
      const result = await api.submitFormByCode(code!, formData);
      console.log('📤 Submission result:', result);
      
      console.log('✅ Form submitted successfully');
      setShowSuccess(true);
      setSession((prev: any) => prev ? { ...prev, isSubmitted: true, submittedAt: new Date().toISOString() } : null);
      
    } catch (error: any) {
      console.error('❌ Error submitting form:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      alert(`Failed to submit form: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualSave = async () => {
    try {
      console.log('💾 Manual save requested...');
      await saveFormData(formData);
      alert('Form saved successfully!');
    } catch (error) {
      console.error('Manual save failed:', error);
      alert('Failed to save form. Please try again.');
    }
  };

  const handleFormChange = (fieldId: string, value: any) => {
    const updatedData = { ...formData, [fieldId]: value };
    setFormData(updatedData);
    // No auto-save - user must manually save
  };

  if (isLoading) {
    return (
      <div className="client-form-loading">
        <div className="loading-spinner"></div>
        <p>Loading your form...</p>
      </div>
    );
  }

  if (error) {
    // Show custom error page for code-related errors
    if (showErrorPage) {
      return (
        <CodeErrorPage 
          error={error}
          code={code || undefined}
          onRetry={() => {
            // Clear current state and navigate to code entry
            setError(null);
            setShowErrorPage(false);
            navigate('/', { replace: true });
          }}
        />
      );
    }
    
    // Show generic error for other types of errors
    return (
      <div className="client-form-error">
        <div className="error-icon">❌</div>
        <h2>Error Loading Form</h2>
        <p>{error}</p>
        <button onClick={() => window.location.href = '/'}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="client-form">
      <div className="form-content">
        {session?.formId ? (
          <PublicIntakeForm 
            formId={session.formId} 
            initialFormData={formData} 
            onFormChange={handleFormChange}
          />
        ) : (
          <div className="form-display">
            <h3>Form Data</h3>
            <p>Form ID: {session?.formId}</p>
            <p>Status: {session?.isSubmitted ? 'Submitted' : 'Draft'}</p>
            
            <div className="form-data-preview">
              <h4>Current Form Data:</h4>
              <pre>{JSON.stringify(formData, null, 2)}</pre>
            </div>
            
            <div className="form-actions">
              <button 
                onClick={() => setFormData({...formData, testField: 'Updated at ' + new Date().toLocaleTimeString()})}
                className="test-btn"
              >
                Update Test Data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientForm; 